import React, { useCallback, useEffect, useState } from 'react';
import Filter from '../../reusable/Client/Filter';
import AddNewClient from '../../reusable/Client/AddNewClient';
import AssignedDriver from '../../reusable/Client/AssignedDriver';
import ManageTask from '../../reusable/Client/ManageTask';
import WorkPerformanceGraph from './Graph/ClientGraph';
import ClientListTable from './ClientListTable';
import axiosInstance from 'config/axiosInstance';
import DeleteClientDialog from '../../reusable/Client/dialog/DeleteClientDialog';
import { useTranslation } from 'react-i18next';

interface ClientManagementProps {
  dateRange: { start: string; end: string };
  onClientHourChange: (value: boolean) => void;
}

interface DeleteClientData {
  id: string;
  clientName: string;
  assign_drivers: string;
  tasks: string;
}

// Helper function to convert time strings to seconds
const timeStringToSeconds = (time: string | null): number => {
  if (!time) return 0;
  // Handle case for "days hours:minutes:seconds" and "hours:minutes:seconds"
  const daysMatch = time.match(/^(\d+)\s(\d{2}):(\d{2}):(\d{2})/);
  if (daysMatch) {
    const [_, days, hours, minutes, seconds] = daysMatch.map(Number);
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  const parts = time.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2]; // hours:minutes:seconds
  }

  return 0;
};

// Helper function to convert seconds back to "days hh:mm:ss" format
const secondsToDaysHHMMSS = (totalSeconds: number): string => {
  const days = Math.floor(totalSeconds / 86400); // Calculate days
  totalSeconds %= 86400;

  const hours = Math.floor(totalSeconds / 3600); // Calculate hours
  totalSeconds %= 3600;

  const minutes = Math.floor(totalSeconds / 60); // Calculate minutes
  const seconds = totalSeconds % 60; // Remaining seconds

  // Format the time with days and ensure hours, minutes, and seconds have two digits
  return `${days} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${seconds.toFixed(6).padStart(9, '0')}`;
};

const ClientManagement: React.FC<ClientManagementProps> = ({
  dateRange,
  onClientHourChange,
}) => {
  const { t } = useTranslation('client');
  const [client, setClient] = useState<any[]>([]);
  const [clientHour, setClientHour] = useState<any[]>([]);
  const [clientNoHour, setClientNoHour] = useState<any[]>([]);
  const [clientNoTask, setClientNoTask] = useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] =
    useState<boolean>(false);
  const [deleteClient, setDeleteClient] = useState<DeleteClientData | null>(
    null
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [showClientHour, setShowClientHour] = useState(true);

  const handleShowClientHourChange = (value: boolean) => {
    setShowClientHour(value);
    onClientHourChange(value);
  };

  const fetchData = useCallback(async () => {
    if (!dateRange.start || !dateRange.end) return;

    try {
      let responseClientUrl = '/v1/client_deliveries/';
      if (selectedClientIds.length > 0) {
        responseClientUrl += `?id__in=${selectedClientIds.join(',')}`;
      }
      const responseClient = await axiosInstance.get(responseClientUrl);
      setClient(responseClient.data.results);

      let responseHourUrl =
        '/v1/client_deliveries/summarize_spent_hour_with_tasks/?';
      if (selectedTaskId) {
        responseHourUrl += `id__in=${selectedTaskId}&`;
      }
      responseHourUrl += `start_time=${dateRange.start}T00%3A00%3A00.000Z&stop_time=${dateRange.end}T23%3A59%3A59.999Z`;
      const responseHour = await axiosInstance.get(responseHourUrl);
      setClientHour(responseHour.data);

      // Sum up the hour_spent for each client and include id, name, order, and formatted time
      const clientTaskSums = responseHour.data.map((client: any) => {
        const totalSeconds = client.summarize.reduce(
          (sum: number, task: any) => {
            return sum + timeStringToSeconds(task.hour_spent);
          },
          0
        );

        return {
          id: client.id,
          name: client.name,
          order: client.order,
          hour_spent: secondsToDaysHHMMSS(totalSeconds),
        };
      });

      setClientNoTask(clientTaskSums);

      let responseNoHourUrl =
        '/v1/client_deliveries/summarize_spent_hour_without_tasks/?';
      if (selectedTaskId) {
        responseNoHourUrl += `id__in=${selectedTaskId}&`;
      }
      responseNoHourUrl += `start_time=${dateRange.start}T00%3A00%3A00.000Z&stop_time=${dateRange.end}T23%3A59%3A59.999Z`;
      const responseNoHour = await axiosInstance.get(responseNoHourUrl);
      setClientNoHour(responseNoHour.data);
    } catch (error) {
      console.error('Failed to fetch client data', error);
    }
  }, [dateRange, selectedClientIds, selectedTaskId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    setHoveredIndex(index);
  };

  const handleDragEnd = async () => {
    if (
      draggedIndex !== null &&
      hoveredIndex !== null &&
      draggedIndex !== hoveredIndex
    ) {
      const updatedClient = [...client];

      const draggedItem = updatedClient[draggedIndex];
      const targetItem = updatedClient[hoveredIndex];
      const tempOrder = draggedItem.order;

      draggedItem.order = targetItem.order;
      targetItem.order = tempOrder;

      updatedClient[draggedIndex] = targetItem;
      updatedClient[hoveredIndex] = draggedItem;
      setClient(updatedClient);

      try {
        await Promise.all([
          updateClientOrder(draggedItem.id, draggedItem.order),
          updateClientOrder(targetItem.id, targetItem.order),
        ]);
        console.log('Successfully updated order on the server');
        await fetchData();
      } catch (error) {
        console.error('Failed to update client order on the server', error);
      }
    }
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  const updateClientOrder = async (id: number, newOrder: number) => {
    try {
      await axiosInstance.patch(`/v1/client_deliveries/${id}/change_order/`, {
        new_order: newOrder,
      });
      console.log(
        `Order for item ID: ${id} updated to ${newOrder} on the server`
      );
    } catch (error) {
      console.error('Failed to update client order', error);
      throw error;
    }
  };

  const handleDeleteClick = (client: any) => {
    setDeleteClient({
      id: client.id,
      clientName: client.name,
      assign_drivers: client.assign_drivers.length + ' people',
      tasks: client.tasks.length.toString(),
    });
    setShowDeleteClientDialog(true);
  };

  const handleTaskSelect = (taskId: string | null, taskName: string) => {
    setSelectedTaskId(taskId);
  };

  const handleClientSelect = (clientIds: string[], clientNames: string[]) => {
    setSelectedClientIds(clientIds);
    setSelectedClientNames(clientNames);
  };

  return (
    <main className="flex flex-col bg-white">
      <div className="flex flex-col px-4 py-2">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col">
            <h4 className="text-subtitle-1 font-medium">{t('graph.title')}</h4>
          </div>
          <div className="flex flex-row items-center space-x-4">
            <Filter
              onTaskSelect={handleTaskSelect}
              onClientSelect={handleClientSelect}
            />
            <AddNewClient />
            <AssignedDriver />
            <ManageTask />
          </div>
        </div>
        <div>
          <WorkPerformanceGraph
            clientHour={clientHour}
            clientNoHour={clientNoTask}
            onShowClientHourChange={handleShowClientHourChange}
            showClientHour={showClientHour}
          />
        </div>
      </div>
      <div className="flex flex-col p-4 space-y-2">
        <div className="flex flex-row">
          <ClientListTable
            clientData={client}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onDeleteClick={handleDeleteClick}
          />
        </div>
      </div>
      {showDeleteClientDialog && deleteClient && (
        <DeleteClientDialog
          id={deleteClient.id}
          name={deleteClient.clientName}
          isOpen={showDeleteClientDialog}
          onClose={() => setShowDeleteClientDialog(false)}
          fields={[
            { label: t('table.client_id'), value: deleteClient.id },
            { label: t('table.client_name'), value: deleteClient.clientName },
            { label: t('table.assignees'), value: deleteClient.assign_drivers },
            { label: t('table.task_counts'), value: deleteClient.tasks },
          ]}
        />
      )}
    </main>
  );
};

export default ClientManagement;
